import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import {
    Crown, Sparkles, Shield, Rocket, Star, Zap, ChevronRight
} from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import PricingModal from './PricingModal';
import toast from 'react-hot-toast';
import { getUserPlan } from '@/utils/plans';
import { cn } from "@/lib/utils";

const LoadingSpinner = () => (
    <div className="flex items-center justify-center">
        <motion.div
            className="relative w-4 h-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
            <div className="absolute w-full h-full border-2 border-amber-200 border-t-amber-500 rounded-full" />
            <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{ scale: [1, 0.8, 1] }}
                transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            >
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
            </motion.div>
        </motion.div>
    </div>
);

const notify = {
    loading: (message) =>
        toast.loading(message, {
            style: {
                background: '#FEF3C7',
                color: '#92400E',
                padding: '16px',
                borderRadius: '8px',
            },
            iconTheme: {
                primary: '#92400E',
                secondary: '#FEF3C7',
            },
        }),
    success: (message) =>
        toast.success(message, {
            style: {
                background: '#F0FDF4',
                color: '#166534',
                padding: '16px',
                borderRadius: '8px',
            },
            duration: 3000,
        }),
    error: (message) =>
        toast.error(message, {
            style: {
                background: '#FEF2F2',
                color: '#991B1B',
                padding: '16px',
                borderRadius: '8px',
            },
            duration: 4000,
        }),
};

const getPlanConfig = (plan) => {
    const configs = {
        Free: {
            text: "Mejorar Plan",
            shortText: "Mejorar",
            icon: Star,
            badgeText: "Free"
        },
        Basic: {
            text: "Plan Básico Plus",
            shortText: "Básico",
            icon: Zap,
            badgeText: "Basic"
        },
        Pro: {
            text: "Plan Pro",
            shortText: "Pro",
            icon: Rocket,
            badgeText: "PRO"
        },
        Premium: {
            text: "Plan Premium",
            shortText: "Premium",
            icon: Crown,
            badgeText: "Premium"
        }
    };
    return configs[plan] || configs.Free;
};

export const SidebarUpgradeButton = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { user, isLoaded } = useUser();
    const isPremium = user?.unsafeMetadata?.isPremium === true;

    if (!isLoaded) return null;

    const plan = getUserPlan(user);
    const planConfig = getPlanConfig(plan);
    const PlanIcon = planConfig.icon;

    const handleSelectPlan = async (plan) => {
        const loadingToast = toast.loading('Procesando tu solicitud...');
        try {
            setIsLoading(true);
            const userEmail = user?.primaryEmailAddress?.emailAddress;

            if (!userEmail) {
                toast.dismiss(loadingToast);
                toast.error('No se encontró el email del usuario.');
                return;
            }

            const response = await axios.post('/api/checkout', {
                userEmail,
                userId: user?.id,
                productId: 'prod_ROSBpbj6Ph5eLz',
                name: plan.name
            });

            if (response.data?.url) {
                toast.dismiss(loadingToast);
                toast.success('¡Todo listo! Redirigiendo al checkout...');

                setTimeout(() => {
                    window.location.assign(response.data.url);
                }, 1000);
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.dismiss(loadingToast);
            toast.error('No se pudo procesar el pago. Por favor, intenta de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Button
                onClick={() => setIsModalOpen(true)}
                disabled={isLoading}
                variant="ghost"
                className={cn(
                    "w-full h-12", // Reduced height from h-14
                    "flex items-center justify-between px-4",
                    "bg-gradient-to-r from-amber-50 to-amber-100/70",
                    "hover:from-amber-100/80 hover:to-amber-200/50",
                    "border border-amber-200/60",
                    "hover:border-amber-300/70",
                    "rounded-xl",
                    "shadow-sm hover:shadow-md",
                    "transition-all duration-200",
                    "group"
                )}
            >
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        {/* Left side with icon and text */}
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <PlanIcon className="h-4.5 w-4.5 text-amber-700 transition-transform duration-200 group-hover:scale-110" />
                                <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-400 rounded-full" />
                            </div>
                            <div className="flex items-center"> {/* Changed to just flex and items-center */}
                                <span className={cn(
                                    "text-[13px] font-medium",
                                    "bg-gradient-to-r from-amber-800 to-amber-700",
                                    "bg-clip-text text-transparent"
                                )}>
                                    {planConfig.shortText}
                                </span>
                            </div>
                        </div>

                        {/* Right side badge */}
                        <div className="flex items-center gap-2">
                            <span className={cn(
                                "text-[11px] px-2 py-0.5 rounded-full",
                                "bg-gradient-to-r from-amber-500/10 to-amber-600/10",
                                "border border-amber-400/20",
                                "text-amber-700 font-medium",
                                "transition-colors duration-200",
                                "group-hover:border-amber-400/30",
                                "group-hover:from-amber-500/15 group-hover:to-amber-600/15"
                            )}>
                                {planConfig.badgeText}
                            </span>
                            <ChevronRight className="h-4 w-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </div>
                    </>
                )}
            </Button>

            <AnimatePresence mode="sync">
                {isModalOpen && (
                    <PricingModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onSelectPlan={handleSelectPlan}
                        isLoading={isLoading}
                        isPremium={isPremium}
                        plan={plan}
                    />
                )}
            </AnimatePresence>
        </>
    );
};

export default SidebarUpgradeButton;